'use client';

import { useRouter } from 'next/navigation';
import { FormEvent, useEffect, useRef, useState } from 'react';

import { homepagePath, type HomepageEditorialCopy } from '@/libs/homepage-editorial-copy';

import styles from './homepage-editorial.module.css';

export default function HomepageSearchPanel({
  locale,
  copy,
  placeholder,
  searchButton,
}: {
  locale: string;
  copy: HomepageEditorialCopy;
  placeholder: string;
  searchButton: string;
}) {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const submitQuery = (value: string) => {
    const normalized = value.trim();
    if (!normalized) return;
    router.push(`${homepagePath(locale, '/search-results')}?q=${encodeURIComponent(normalized)}&locale=${encodeURIComponent(locale)}`);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitQuery(query);
  };

  useEffect(() => {
    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      const target = event.target;
      const typing = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || (target instanceof HTMLElement && target.isContentEditable);
      if (event.key === '/' && !typing) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, []);

  return (
    <form className={styles.searchPanel} role="search" onSubmit={handleSubmit}>
      <label className={styles.visuallyHidden} htmlFor="homepage-tool-search">{copy.searchLabel}</label>
      <div className={styles.searchBox}>
        <input
          ref={inputRef}
          id="homepage-tool-search"
          type="search"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
        />
        <button type="submit" disabled={!query.trim()}>{searchButton}</button>
      </div>
    </form>
  );
}
